import { memo } from 'preact/compat'
import { useState, useEffect, useContext } from 'preact/hooks'
import { getStylesFn } from 'core/helpers/styles'
import { ElementLogoOptions } from 'core/types/element/element_logo'
import optionsPropTypes from 'PropsSpec'
import WidgetContext from '../Context'

const extractStyles = getStylesFn(['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'], {
  transition: 'opacity 90ms',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer'
})

const extractBackStyles = getStylesFn(['color'], {
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14
})

const extractBackTextStyles = getStylesFn(['fontFamily', 'fontWeight'], {
  marginLeft: '8px',
  fontSize: 'inherit',
  color: 'inherit'
})

const actions = {
  none: 'none',
  goToUrl: 'go_to_url'
}

type Props = {
  options: ElementLogoOptions
}

const Logo = (props: Props) => {
  const { options } = props
  const [loaded, setLoaded] = useState(false)

  const { newPrevStep, prevStep } = useContext(WidgetContext)

  const onPrevClick = () => {
    prevStep()
  }

  useEffect(() => {
    const intervalId = setTimeout(() => {
      setLoaded(true)
    }, 500)

    return () => clearTimeout(intervalId)
  }, [options.url])

  const onImageLoad = () => {
    setLoaded(true)
  }

  const styles = {
    ...extractStyles(options),
    opacity: loaded ? 1 : 0
  }

  const backStyles = {
    ...extractBackStyles(options),
    top: options.padding_top,
    left: options.padding_left
  }

  const imgStyles = {
    height: options.size,
    objectFit: 'contain',
    cursor: options.action === actions.goToUrl ? 'pointer' : 'default'
  }

  const backTextStyles = extractBackTextStyles(options)

  const onClick = () => {
    switch (options.action) {
      case actions.goToUrl:
        if (options.action_go_to_url) {
          window.open(options.action_go_to_url, '_blank')?.focus()
        }
        break
      case actions.none:
      default:
        break
    }
  }

  return (
    <div style={styles} className="wf_reset wf_logo_wrapper">
      {newPrevStep && (
        <div style={backStyles} className="wf_reset wf_logo_back" onClick={onPrevClick}>
          <BackIcon className="wf_reset wf_logo_back_icon" />
          <span style={backTextStyles}>Back</span>
        </div>
      )}
      <img onClick={onClick} onLoad={onImageLoad} style={imgStyles} className="wf_reset wf_image" src={options.url} />
    </div>
  )
}

const BackIcon = ({ className }: { className: string }) => (
  <svg className={className} width="1em" height="0.7em" viewBox="0 0 13 9">
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <g transform="translate(-468.000000, -77.000000)" fill="currentColor" fillRule="nonzero">
        <g transform="translate(468.000000, 74.000000)">
          <path
            d="M12.422442,6.97238333 L8.6746892,3.22463051 C8.44602943,2.99597075 8.07518929,2.99597075 7.84652952,3.22463051 C7.61782291,3.45333713 7.61782291,3.82408358 7.84652952,4.05279019 L10.5946161,6.80087679 L0.585586377,6.80087679 C0.262202156,6.80087679 -2.66453526e-15,7.06307894 -2.66453526e-15,7.38646317 C-2.66453526e-15,7.70980054 0.262202156,7.97204954 0.585586377,7.97204954 L10.5946161,7.97204954 L7.84662322,10.7201361 C7.6179166,10.9488428 7.6179166,11.3195892 7.84662322,11.5482958 C7.96092968,11.6625554 8.11083979,11.7198024 8.26070306,11.7198024 C8.41056632,11.7198024 8.56042959,11.6625554 8.67478289,11.5482958 L12.422442,7.800543 C12.6511486,7.57183639 12.6511486,7.20108994 12.422442,6.97238333 Z"
            transform="translate(6.296986, 7.386469) scale(-1, 1) translate(-6.296986, -7.386469) "
          ></path>
        </g>
      </g>
    </g>
  </svg>
)

Logo.propTypes = {
  options: optionsPropTypes('logo')
}

export default memo(Logo)
